/**
 * `Retry-After` reading for the two HTTP clients.
 *
 * WHY THIS EXISTS (2026-09-05 Mongo write-stall incident; MONGO-STALL-PLAN-v5.2.md
 * §4.5, correction 19)
 *   There is no `Retry-After` handling anywhere on the client today, so
 *   professorx has no way to ask a batch to slow down: one user's 340-file
 *   convert arrived as 340 concurrent schedule calls at a stalled primary, and
 *   each failure was followed by two blind 1 s retries. The M4 backpressure
 *   contract answers **503** with `Retry-After: <seconds>` and a
 *   `{code:'service_busy', retryAfterSeconds}` body, and **409** with
 *   `Retry-After: 2` and `{code:'schedule_in_flight'}`.
 *
 * A LEAF module on purpose: no service, no rxjs, no DOM types beyond the
 * structural `getAllResponseHeaders`. Both `LightHttp` (rxjs/ajax) and
 * `HttpClient` (axios) call it from their error converters, so the two clients
 * cannot drift on what "the server asked me to wait" means.
 */

/**
 * Anything with an `xhr` that can list its response headers, and/or a parsed
 * body. Structural so an AjaxError, an AxiosError and a test double all fit.
 */
export interface RetryAfterSource {
    xhr?: { getAllResponseHeaders?: () => string } | null;
    response?: any;
    headers?: Record<string, any> | null;
}

/**
 * `Retry-After` in whole seconds, or `undefined` when the server said nothing
 * this client is willing to trust. Never returns 0 by default: "wait 0 s" and
 * "no instruction" must not be the same value to the retry policy.
 *
 * Three deliberate details:
 *
 *  1. The header is read out of `getAllResponseHeaders()`, never
 *     `getResponseHeader('Retry-After')`. Retry-After is NOT CORS-safelisted and
 *     every API call we make is cross-origin (the api. subdomain), so the
 *     targeted getter logs `Refused to get unsafe header` on every response —
 *     the same trap the clock-skew `Date` read documents in light-http.service.
 *     `getAllResponseHeaders()` simply omits what is not exposed and logs
 *     nothing.
 *  2. **The body fallback is not optional.** Until professorx lists
 *     `Retry-After` in `Access-Control-Expose-Headers`, the header is invisible
 *     to the browser on every cross-origin response, and `retryAfterSeconds` in
 *     the 503/409 body is the only channel that reaches us.
 *  3. `Date.parse` is not pointed at the raw header. V8's legacy fallback parser
 *     turns `-5` into a real date (2001-05-01), which would silently become
 *     "retry immediately" — the exact opposite of what a server under load
 *     asked for. Only a plain non-negative integer, or a string that actually
 *     looks like an HTTP-date (4-digit year AND a letter: the day/month name of
 *     an IMF-fixdate, or the T/Z of an ISO-8601 string), is accepted. Verified
 *     against V8 on 2026-09-05.
 */
export function readRetryAfterSeconds(
    source: RetryAfterSource | null | undefined,
    nowMs: number = Date.now(),
): number | undefined {
    try {
        if (!source) return undefined;
        const raw = rawHeaderValue(source);
        if (raw) {
            const parsed = parseRetryAfterHeader(raw, nowMs);
            if (parsed !== undefined) return parsed;
        }
        const fromBody = source.response?.retryAfterSeconds;
        if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody >= 0) {
            return Math.round(fromBody);
        }
    } catch { /* header parsing must never break an error path */ }
    return undefined;
}

/** Parse one raw header value. Exported for the junk-rejection spec. */
export function parseRetryAfterHeader(
    value: string | null | undefined,
    nowMs: number = Date.now(),
): number | undefined {
    if (value === null || value === undefined) return undefined;
    const trimmed = String(value).trim();
    if (trimmed === '') return undefined;

    // delta-seconds: a plain non-negative integer and nothing else. "30s",
    // "-5", "1e3" and "12.5" are junk, not instructions.
    if (/^\d+$/.test(trimmed)) return Number(trimmed);

    if (!/\d{4}/.test(trimmed) || !/[A-Za-z]/.test(trimmed)) return undefined;
    const parsed = Date.parse(trimmed);
    if (Number.isNaN(parsed)) return undefined;
    return Math.max(0, Math.round((parsed - nowMs) / 1000));
}

function rawHeaderValue(source: RetryAfterSource): string {
    const all = source.xhr?.getAllResponseHeaders?.() || '';
    const match = /^retry-after:\s*(.+)$/im.exec(all);
    if (match) return match[1].trim();
    // axios exposes a plain headers object rather than an xhr.
    const headers = source.headers;
    if (headers && typeof headers === 'object') {
        const key = Object.keys(headers).find(k => k.toLowerCase() === 'retry-after');
        if (key && headers[key] != null) return String(headers[key]).trim();
    }
    return '';
}
